import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FilterStoreService } from '../../../../services/filter-store.service';
import { PageIndexService } from '../../../../services/page-index-service';
import { SubProjectImmoPrint } from '../../../../model/project/sub-project-immo-print';
import { AbstractImmoOverviewComponent } from '../abstract-immo-overview.component';
import { SubProjectControllerRequestService } from '../../../../services/api/sub-project-controller-request.service';
import { Lightbox } from 'ngx-lightbox';
import { Router } from '@angular/router';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import DateHelper from '../../../../helpers/date-helper';
import { BoardTypeChoices } from '../../../../model/product/board/board-part';
import { cloneDeep } from 'lodash';
import { SnackService } from '../../../../services/snack.service';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { TourHelper } from 'src/app/helpers/tour-helper';
import { SUB_PROJECT_TYPES } from 'src/app/model/project/sub-project';
import { ImmoPrintIpBoard } from 'src/app/model/project/immo-print-ip-board';
import { ClipboardWrapperService } from 'src/app/services/utility/clipboard/clipboard-wrapper.service';
import { StringHelper } from 'src/app/helpers/string-helper';

@Component({
  selector: 'print-immo-overview',
  templateUrl: '../abstract-immo-overview.component.html',
  styleUrls: ['../abstract-immo-overview.component.scss'],
})
export class PrintImmoOverviewComponent
  extends AbstractImmoOverviewComponent<SubProjectImmoPrint>
  implements OnInit
{
  @Output()
  countChange = new EventEmitter<number>();

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  constructor(
    protected filterStoreService: FilterStoreService,
    protected pageIndexService: PageIndexService,
    protected cd: ChangeDetectorRef,
    protected subProjectService: SubProjectControllerRequestService,
    protected _lightbox: Lightbox,
    protected router: Router,
    private snackService: SnackService,
    private clipboardWrapperService: ClipboardWrapperService
  ) {
    super(
      [
        {
          key: 'project__id',
          active: false,
          strictEquality: true,
          numeric: true,
        },
        {
          key: 'immoNewCustomerName',
          active: false,
          strictEquality: false,
          numeric: false,
        },
      ],
      filterStoreService,
      pageIndexService,
      cd,
      subProjectService,
      _lightbox,
      router
    );
  }

  ngOnInit() {
    super.ngOnInit();

    this.hasSaveFunctionality = true;
  }

  public override openTour(subProject: SubProjectImmoPrint): void {
    throw new Error('Method not implemented.');
  }
  protected override getPaginator(): MatPaginator {
    return this.paginator;
  }

  protected override getLocalStorageKey(): string {
    return 'PrintImmoOverviewComponent';
  }

  protected override initDisplayedColumns(): void {
    this.displayedColumns = [
      'copyableProjectId',
      'placementDate',
      'installer',
      'branch',
      'customerName',
      'printWorkExtraInfo',
      'printAmount',
      'kind',
      'measurements',
      'design',
      'displayStatus',
      'markSubProjectAsDoneAction',
    ];
  }

  protected override initSortSettings(): void {
    this.sortColumn = 'project.currentRequestDate';
    this.sortAscending = false;
  }

  protected override fetchItems(): void {
    this.loading = true;
    this.items = [];

    this.subProjectService
      .getImmoPrint(
        this.generateFilterString(),
        this.sortColumn,
        this.sortAscending,
        this.paginator.pageSize,
        this.paginator.pageIndex,
        false
      )
      .then((res) => {
        this.processPageResult(res);
        this.totalItems = res.totalItems;
        this.countChange.emit(this.totalItems);
        this.items = this.parseFetchedItems(res.items);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private parseFetchedItems(
    items: SubProjectImmoPrint[]
  ): SubProjectImmoPrint[] {
    const parsedItems: SubProjectImmoPrint[] = [];
    for (const item of items) {
      this.calculateStatusClass(item);

      if (item.tourItem != null) {
        item.currentConfirmedPlacementDateDisplay =
          TourHelper.getTourDateDisplay(item.tourItem);
      }

      const splitItems: SubProjectImmoPrint[] = [];

      if (
        item.type === SUB_PROJECT_TYPES.BASE_PROJECT &&
        item.project.ipBoards != null &&
        item.project.ipBoards.length > 0
      ) {
        item.project.ipBoards.forEach((ipBoard) => {
          //Skip completely if already registered (this can happen due to limitations of ToMany filtering / association fetching)
          if (!ipBoard.isRegistered) {
            const splitItem: SubProjectImmoPrint = cloneDeep(item);

            splitItem.project.firstIpBoard = ipBoard;
            splitItem.project.firstIpBoard.tablePrintWorkItems =
              ipBoard.leftPrintWorkItems;

            if (
              CollectionHelper.isPopulated(ipBoard.leftPrintWorkItems) ||
              !this.willSplitOnRightPrintworkItems(splitItem, ipBoard)
            ) {
              splitItems.push(splitItem);
            }

            if (
              splitItem.project.firstIpBoard.boardType != null &&
              (splitItem.project.firstIpBoard.boardType.ordinal ===
                BoardTypeChoices.V.ordinal ||
                splitItem.project.firstIpBoard.boardType.ordinal ===
                  BoardTypeChoices.DOUBLE_SIDED.ordinal)
            ) {
              if (CollectionHelper.isPopulated(ipBoard.rightPrintWorkItems)) {
                //Split again
                const rightSplitItem = cloneDeep(splitItem);
                rightSplitItem.project.firstIpBoard.tablePrintWorkItems =
                  ipBoard.rightPrintWorkItems;
                splitItems.push(rightSplitItem);
              } else {
                if (splitItem.project.firstIpBoard.kind !== 'Kleefbord') {
                  splitItem.project.firstIpBoard.amount = 2;
                }
              }
            }
          }
        });
      } else if (CollectionHelper.isPopulated(item.project.ipBoards)) {
        item.project.firstIpBoard = item.project.ipBoards[0];

        if (
          item.type === SUB_PROJECT_TYPES.SOLDRENTED &&
          item.project.firstIpBoard.kind !== 'Kleefbord' &&
          item.project.firstIpBoard.boardType != null &&
          (item.project.firstIpBoard.boardType.ordinal ===
            BoardTypeChoices.V.ordinal ||
            item.project.firstIpBoard.boardType.ordinal ===
              BoardTypeChoices.DOUBLE_SIDED.ordinal)
        ) {
          item.project.firstIpBoard.amount = 2;
        }

        splitItems.push(item);
      }
      //Do not write any fields after this point. All frontend only fields should be set before the split occurs!

      splitItems.forEach((si) => parsedItems.push(si));
    }

    return parsedItems;
  }

  private willSplitOnRightPrintworkItems(
    item: SubProjectImmoPrint,
    ipBoard: ImmoPrintIpBoard
  ): boolean {
    return (
      item.project.firstIpBoard.boardType != null &&
      (item.project.firstIpBoard.boardType.ordinal ===
        BoardTypeChoices.V.ordinal ||
        item.project.firstIpBoard.boardType.ordinal ===
          BoardTypeChoices.DOUBLE_SIDED.ordinal) &&
      CollectionHelper.isPopulated(ipBoard.rightPrintWorkItems)
    );
  }

  private calculateStatusClass(item: SubProjectImmoPrint): void {
    if (
      item.tourItem == null ||
      item.tourItem.tour == null ||
      item.tourItem.tour.atelierDate == null
    )
      return;

    this.calculateStatusClassForAtelierDate(
      item,
      item.tourItem.tour.atelierDate
    );
  }

  private calculateStatusClassForAtelierDate(
    item: SubProjectImmoPrint,
    atelierDate: Date
  ): void {
    const atelierDateMoment = DateHelper.parseMoment(atelierDate);

    if (atelierDateMoment.isSameOrBefore(this.today, 'day')) {
      item.statusClass = 'print-deadline-passed';
    } else if (
      atelierDateMoment.isSame(
        DateHelper.parseMoment(this.today).add(1, 'days'),
        'day'
      )
    ) {
      item.statusClass = 'print-deadline-soon';
    }
  }

  public override openProject(subProject: SubProjectImmoPrint) {
    this.router.navigate([
      '/project',
      subProject.project == null ? null : subProject.project.id,
      subProject.id,
    ]);
  }

  // override
  public override copyProjectId(subProject: SubProjectImmoPrint): void {
    let toCopyText = '';
    if (subProject.project != null && subProject.project.id != null) {
      toCopyText = subProject.project.id.toString();
    }
    if (subProject.tourItem != null && subProject.tourItem.tour != null) {
      let placerName = null;
      let dateString = null;
      if (
        subProject.tourItem.tour.placer != null &&
        subProject.tourItem.tour.placer.user != null
      ) {
        placerName = StringHelper.join(
          [
            subProject.tourItem.tour.placer.user.firstName == null
              ? null
              : subProject.tourItem.tour.placer.user.firstName.toString()[0],
            subProject.tourItem.tour.placer.user.lastName == null
              ? null
              : subProject.tourItem.tour.placer.user.lastName.toString()[0],
          ],
          ''
        );
      }
      if (subProject.tourItem.tour.date != null) {
        dateString = DateHelper.formatDate(subProject.tourItem.tour.date);
      }
      toCopyText = StringHelper.join([toCopyText, placerName, dateString], ' ');
    }
    if (!StringHelper.isEmptyOrWhiteSpace(toCopyText)) {
      this.clipboardWrapperService.copyToClipboard(toCopyText);
    }
  }

  save(): void {
    const subProjectIds: number[] = this.items
      .filter((item) => item.markedAsDone)
      .map((item) => item.id);

    if (subProjectIds.length === 0) {
      return;
    }

    this.subProjectService.immoPrintRegister(subProjectIds).then(() => {
      this.snackService.showSuccess('Registraties succesvol opgeslagen');
      this.fetchItems();
    });
  }
}
